import React, { useState, useEffect } from "react";
import { Button, Drawer, Form, Select } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { GetAllCountryApi } from "../../../../api/request/common.js";
import TableWithSkeleton from "../../../../components/table/TableWithSkeleton.jsx";
import CorporateBeneficiaryForm from "./CorporateBeneficiaryForm.jsx";
import IndividualBeneficiaryForm from "./IndividualBenificiaryForm.jsx";
import {
  addUpdateBeneficialInfo,
  getTaxDomicilesReasons,
  getNatureOfBusiness,
  getAllBeneficialInfoApi,
  getBeneficialInfoApi,
  deleteBeneficialInfoApi,
} from "../../../../api/request/corporate.js";
import { ProofTypeOptions } from "../../../../utils/constant.js";
import toast from "react-hot-toast";
import { handleApiError } from "../../../../utils/handleApiError.js";
import dayjs from "dayjs";
import ConfirmationModal from "../../../../components/modals/ConfirmationModal.jsx";
import Loader from "../../../../components/Loader.jsx";
import api from "../../../../api/index.js";
import { useColumns } from "../../../../columns/documentColumns.jsx";
import { useTranslation } from "../../../../utils/onboardingI18n";

const BeneficiaryInfo = ({ onNext, onPrevious, isSubmitted }) => {
  const { t } = useTranslation("onBoarding");
  const [form] = Form.useForm();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [beneficiariesList, setBeneficiariesList] = useState([]);
  const [beneficiaryType, setBeneficiaryType] = useState(null);
  const [countries, setCountries] = useState([]);
  const [countryCodes, setCountryCodes] = useState([]);
  const [hasOtherCrsTaxResident, setHasOtherCrsTaxResident] = useState(false);
  const [additionalCrsTaxResidents, setAdditionalCrsTaxResidents] = useState(
    []
  );
  const [sameAsAbove, setSameAsAbove] = useState(false);
  const [isUSPerson, setIsUSPerson] = useState(null);
  const [acceptDeclaration, setAcceptDeclaration] = useState(false);
  const [isPEP, setIsPEP] = useState(null);
  const [documentFileList, setDocumentFileList] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [proofType, setProofType] = useState(null);
  const [documentSubType, setDocumentSubType] = useState(null);
  const [taxDomicileReasons, setTaxDomicileReasons] = useState([]);
  const [natureOfBusinessApiOptions, setNatureOfBusinessApiOptions] = useState(
    []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDeleteConfirmModal, setIsDeleteConfirmModal] = useState(false);
  const [selectedBeneficiaryForDelete, setSelectedBeneficiaryForDelete] =
    useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isDisabled = isSubmitted;

  const getCountries = async () => {
    try {
      const { data, i } = await GetAllCountryApi();
      if (data?.success) {
        setCountries(
          data?.data?.map((item, i) => ({
            key: i,
            value: item?.id,
            label: item?.countryName,
          }))
        );

        setCountryCodes(
          data?.data
            ?.map((item, i) => ({
              key: i,
              value: (item?.dialCode || item?.countryCode1),
              label: `${(item?.dialCode || item?.countryCode1)} (${item?.countryCode2})`,
              countryCode: (item?.dialCode || item?.countryCode1),
            }))
            ?.filter((item) => item?.value)
        );
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  const getTaxDomicileReasonsData = async () => {
    try {
      const { data } = await getTaxDomicilesReasons();
      if (data?.success) {
        const reasons = data?.data?.map((item) => ({
          value: item?.id,
          label: item?.name,
        }));
        setTaxDomicileReasons(reasons);
      }
    } catch (error) {
      console.error("Error fetching tax domicile reasons:", error);
    }
  };

  const getNatureOfBusinessData = async () => {
    try {
      const { data } = await getNatureOfBusiness();
      if (data?.success) {
        setNatureOfBusinessApiOptions(
          data?.data?.map((item) => ({ value: item?.id, label: item?.name }))
        );
      }
    } catch (error) {
      console.error("Error fetching nature of business:", error);
    }
  };

  const getAllBeneficialInfoData = async () => {
    setIsLoading(true);
    try {
      const { data } = await getAllBeneficialInfoApi();
      if (data?.success) {
        setBeneficiariesList(data?.data);
      }
    } catch (error) {
      console.error("Error fetching beneficial info:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getCountries();
    getTaxDomicileReasonsData();
    getNatureOfBusinessData();
    getAllBeneficialInfoData();
  }, []);

  const filterOption = (input, option) => {
    return (option?.label ?? "").toLowerCase().includes(input.toLowerCase());
  };

  const handleAction = (action, record) => {
    if (action === "download") {
      handleDownload(record.file, record.fileName);
    } else if (action === "delete") {
      handleDeleteDocument(record.id);
    }
  };

  const documentColumns = useColumns(handleAction);

  const beneficiariesColumns = [
    {
      title: t("Name"),
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <span>
          {record.type === 2 ? t("Company Name") : t("Beneficiary Name")}:{" "}
          {record.name || "N/A"}
        </span>
      ),
    },
    {
      title: t("Email Id"),
      dataIndex: "email",
      key: "email",
    },
    {
      title: t("Type"),
      dataIndex: "type",
      key: "type",
      render: (type) => (
        <span className="capitalize">
          {type === 1 ? t("Individual") : t("Corporate")}
        </span>
      ),
    },
    {
      title: t("Action"),
      key: "action",
      render: (_, record) => {
        const index = beneficiariesList.findIndex((b) => b.id === record.id);
        return (
          <div className="flex gap-2">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => !isDisabled && handleEdit(record?.id, index)}
              disabled={isDisabled}
              className="flex items-center justify-center"
            />
            {beneficiariesList?.length > 1 && (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteClick(record)}
                className="flex items-center justify-center"
                disabled={isDisabled}
              />
            )}
          </div>
        );
      },
    },
  ];

  const handleTypeChange = (value) => {
    setBeneficiaryType(value);
  };

  const handleUSPersonChange = (e) => {
    const value = e.target.value;
    setIsUSPerson(value);
    setAcceptDeclaration(false);
    if (value) {
      form.setFieldsValue({ socialSecurityNumber: "" });
    }
  };

  const handlePEPChange = (e) => {
    const value = e.target.value;
    setIsPEP(value);
    if (!value) {
      form.setFieldsValue({ pepDetails: "" });
    }
  };

  const openPdf = (url) => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const getFileUrl = (file) => {
    if (!file) return "";
    if (file.originFileObj) return URL.createObjectURL(file.originFileObj);
    return "";
  };

  const handleDocumentChange = ({ fileList }) => {
    if (!fileList.length) return;

    const fileObj = fileList[0]?.originFileObj;
    if (!fileObj) return;

    setDocumentFileList(fileList);
    setSelectedFile(fileObj);
    setPreviewUrl(URL.createObjectURL(fileObj));
  };
  
  const handleAddDocument = (e) => {
    e.preventDefault();
    if (!selectedFile || !proofType || !documentSubType) return;

    addToUploadedDocuments(proofType, documentSubType, {
      originFileObj: selectedFile,
    });

    setSelectedFile(null);
    setPreviewUrl(null);
    setDocumentFileList([]);
    form.setFieldsValue({ proofType: null });
    form.setFieldsValue({ documentSubType: null });
  };

  const handleRemoveDocument = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setDocumentFileList([]);
  };

  const handleProofTypeChange = (value) => {
    setProofType(value);
    form.setFieldsValue({ proofType: value });
    setDocumentSubType(null);
    form.setFieldsValue({ documentSubType: null });
  };

  const handleDocumentSubTypeChange = (value) => {
    setDocumentSubType(value);
    form.setFieldsValue({ documentSubType: value });
  };

  const addToUploadedDocuments = (proofTypeValue, subTypeValue, file) => {
    const selectedProof = ProofTypeOptions.find(
      (opt) => opt.value === proofTypeValue
    );
    const proofTypeLabel = selectedProof?.label || proofTypeValue;
    const subTypeLabel =
      (selectedProof?.subTypeOptions || []).find(
        (opt) => opt.value === subTypeValue
      )?.label || subTypeValue;

    const newDoc = {
      id: Date.now() + Math.random(),
      documentType: proofTypeLabel,
      documentName: subTypeLabel,
      fileName: file.originFileObj?.name || file.name,
      file: file,
      status: "PROCESSING",
      proofType: proofTypeValue,
      subType: subTypeValue,
    };

    setUploadedDocuments((prev) => [...prev, newDoc]);
  };

  const handleDeleteDocument = (id) => {
    if (isDisabled) return;
    setUploadedDocuments((prev) => prev.filter((doc) => doc.id !== id));
    const deletedDoc = uploadedDocuments.find((doc) => doc.id === id);
    if (deletedDoc && documentFileList.length > 0) {
      const currentFile = documentFileList[0];
      if (currentFile.originFileObj?.name === deletedDoc.fileName) {
        setDocumentFileList([]);
      }
    }
  };

  const handleDownload = async (file, suggestedName) => {
    if (file?.originFileObj) {
      const url = URL.createObjectURL(file.originFileObj);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.originFileObj.name || suggestedName || "document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }
    if (file?.url) {
      try {
        let response;
        if (file.url.startsWith("http://") || file.url.startsWith("https://")) {
          const link = document.createElement("a");
          link.href = file.url;
          link.download = suggestedName || "document";
          link.target = "_blank";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return;
        } else {
          response = await api.get(file.url, { responseType: "blob" });
          const blobUrl = URL.createObjectURL(response.data);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = suggestedName || file.name || "document";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        }
      } catch (err) {
        handleApiError(err);
      }
    }
  };

  const handleSameAsAboveChange = (e) => {
    const checked = e.target.checked;
    setSameAsAbove(checked);

    if (checked) {
      const registeredAddress = form.getFieldsValue([
        "registeredOfficeAddress",
        "registeredStreetName",
        "registeredCountry",
        "registeredCityTown",
        "registeredPostalZipCode",
      ]);

      form.setFieldsValue({
        correspondenceOfficeAddress: registeredAddress.registeredOfficeAddress,
        correspondenceStreetName: registeredAddress.registeredStreetName,
        correspondenceCountry: registeredAddress.registeredCountry,
        correspondenceCityTown: registeredAddress.registeredCityTown,
        correspondencePostalZipCode: registeredAddress.registeredPostalZipCode,
      });
    }
  };

  const handleRegisteredAddressChange = () => {
    if (sameAsAbove) {
      const registeredAddress = form.getFieldsValue([
        "registeredOfficeAddress",
        "registeredStreetName",
        "registeredCountry",
        "registeredCityTown",
        "registeredPostalZipCode",
      ]);

      form.setFieldsValue({
        correspondenceOfficeAddress: registeredAddress.registeredOfficeAddress,
        correspondenceStreetName: registeredAddress.registeredStreetName,
        correspondenceCountry: registeredAddress.registeredCountry,
        correspondenceCityTown: registeredAddress.registeredCityTown,
        correspondencePostalZipCode: registeredAddress.registeredPostalZipCode,
      });
    }
  };

  const handleAdd = () => {
    setEditingIndex(null);
    setBeneficiaryType(null);
    setHasOtherCrsTaxResident(false);
    setAdditionalCrsTaxResidents([]);
    setSameAsAbove(false);
    setIsUSPerson(null);
    setAcceptDeclaration(false);
    setIsPEP(null);
    setDocumentFileList([]);
    setUploadedDocuments([]);
    setProofType(null);
    setDocumentSubType(null);
    form.resetFields();
    form.setFieldsValue({ type: 2 });
    setBeneficiaryType(2);
    setDrawerOpen(true);
  };

  const handleMultiTaxResidentChange = (e) => {
    const isMultiTaxDomicile = e?.target?.value;
    setHasOtherCrsTaxResident(isMultiTaxDomicile);
    const taxDomiciles = form.getFieldValue("taxDomiciles") || [];

    if (isMultiTaxDomicile) {
      if (!taxDomiciles?.length) {
        form.setFieldsValue({
          taxDomiciles: [
            {
              taxDomicileCountryId: null,
              taxIDNumber: "",
              isTINIDAvailable: false,
              taxDomicileReasonID: null,
            },
          ],
        });
      }
    } else {
      if (taxDomiciles?.length) {
        form.setFieldsValue({
          taxDomiciles: [taxDomiciles[0]],
        });
      }
    }
  };

  const handleEdit = async (id, index) => {
    try {
      setDrawerOpen(true);
      setEditingIndex(index);
      setLoading(true);
      const { data } = await getBeneficialInfoApi(id);
      if (data?.success) {
        var beneficiary = data?.data;
      }

      const type = beneficiary?.customerTypeId;
      setBeneficiaryType(type);

      const crsNoTin = beneficiary.crsNoTin || false;
      const hasOtherCrs = beneficiary.hasOtherCrsTaxResident === "yes";
      setHasOtherCrsTaxResident(hasOtherCrs);
      if (hasOtherCrs && beneficiary.additionalCrsTaxResidents) {
        setAdditionalCrsTaxResidents(
          beneficiary.additionalCrsTaxResidents.map((r) => ({
            noTinChecked: r.noTinChecked || false,
          }))
        );
      }

      setSameAsAbove(beneficiary.isSameAsAbove || false);

      const usPersonValue = beneficiary.areYouUSPerson;
      setIsUSPerson(usPersonValue);
      setAcceptDeclaration(true);

      const pepValue = beneficiary.isPoliticallyExposedPerson;
      setIsPEP(pepValue);
      const phone = beneficiary?.mobileNumber ?? "";
      const [mobileCountryCode, mobileNumber] = phone.trim().split(" ");
      const alternativePhone = beneficiary?.alternativePhoneNumber ?? "";
      const [alternativeMobileCountryCode, alternativeMobileNumber] =
        alternativePhone.trim().split(" ");
      const companyPhone = beneficiary?.companyPhoneNumber ?? "";
      const [companyMobileCountryCode, companyMobileNumber] = companyPhone
        .trim()
        .split(" ");
      form.setFieldsValue({
        type: type,
        companyName: beneficiary.companyName,
        dateOfIncorporation: beneficiary.dateOfIncorporation
          ? dayjs(beneficiary.dateOfIncorporation)
          : null,
        countryOfIncorporation: beneficiary.countryOfIncorporation,
        companyRegistrationNumber: beneficiary.companyRegistrationNumber,
        ownershipPercentage:
          beneficiary.ownershipPercentage === null ||
          beneficiary.ownershipPercentage === undefined ||
          beneficiary.ownershipPercentage === ""
            ? undefined
            : Number(beneficiary.ownershipPercentage),
        natureOfBusiness: beneficiary.natureOfBusinessId,
        briefDescription: beneficiary.businessActivity,
        companyWebsite: beneficiary.companyWebsite,
        companyEmail: beneficiary.companyEmail,
        phoneCountryCode: companyMobileCountryCode,
        phoneNumber: companyMobileNumber,
        hasBusinessInUS: beneficiary.isBusinessInUS,
        companyTaxResidency: beneficiary.companyTaxResidency,
        legalEntityIdentifier: beneficiary.legalEntityIdentifier,
        firstName: beneficiary.firstName,
        lastName: beneficiary.lastName,
        dateOfBirth: beneficiary.dob ? dayjs(beneficiary.dob) : null,
        mobileCountryCode: mobileCountryCode,
        mobileNumber: mobileNumber,
        alternativeMobileCountryCode: alternativeMobileCountryCode,
        alternativeMobileNumber: alternativeMobileNumber,
        email: beneficiary.email,
        nationality: beneficiary.nationality,
        countryOfResidence: beneficiary.countryOfResidence,
        countryOfBirth: beneficiary.countryOfBirth,
        countryOfTaxResidence: beneficiary.countryOfTaxResidence,
        crsSelectCountry: beneficiary.crsSelectCountry,
        crsTaxIdentificationNumber: beneficiary.crsTaxIdentificationNumber,
        crsNoTin: crsNoTin,
        crsTinReason: beneficiary.crsTinReason,
        taxDomiciles: beneficiary?.taxDomiciles?.map((t) => ({
          taxDomicileCountryId: t.taxDomicileCountryId,
          taxIDNumber: t.taxIDNumber,
          isTINIDAvailable: t.isTINIDAvailable,
          taxDomicileReasonID: t.taxDomicileReasonID,
          alternateTaxIdentifier: t.alternateTaxIdentifier ?? "",
        })),
        isMultiTaxDomicileEnabled: beneficiary.isMultiTaxDomicileEnabled,
        ...(hasOtherCrs && beneficiary.additionalCrsTaxResidents
          ? beneficiary.additionalCrsTaxResidents.reduce((acc, r, idx) => {
              acc[`additionalCrsSelectCountry_${idx}`] = r.country;
              acc[`additionalCrsTaxIdentificationNumber_${idx}`] = r.taxId;
              acc[`additionalCrsNoTin_${idx}`] = r.noTinChecked || false;
              acc[`additionalCrsTinReason_${idx}`] = r.tinReason;
              return acc;
            }, {})
          : {}),
        isUSPerson: usPersonValue,
        acceptDeclaration: true,
        socialSecurityNumber: beneficiary.socialSecurityNumber,
        isPEP: pepValue,
        pepDetails: beneficiary.pepDetails,
        registeredOfficeAddress: beneficiary.companyApartmentVillaInfo,
        registeredStreetName: beneficiary.companyStreetAreaInfo,
        registeredCountry: beneficiary.companyCountryCode,
        registeredCityTown: beneficiary.companyCity,
        registeredPostalZipCode: beneficiary.companyZipCode,
        sameAsAbove: beneficiary.isSameAsAbove,
        correspondenceOfficeAddress: beneficiary.businessApartmentVillaInfo,
        correspondenceStreetName: beneficiary.businessStreetAreaInfo,
        correspondenceCountry: beneficiary.businessCountryCode,
        correspondenceCityTown: beneficiary.businessCity,
        correspondencePostalZipCode: beneficiary.businessZipCode,
        apartmentVillaNumber: beneficiary.apartmentVillaInfo,
        registeredAddressCountry: beneficiary.countryCode,
        postalZipCode: beneficiary.postalZipCode,
        streetNameArea: beneficiary.streetAreaInfo,
        cityTown: beneficiary.cityTown,
        proofType: beneficiary.proofType,
        documentSubType: beneficiary.documentSubType,
      });

      setProofType(beneficiary.proofType);
      setDocumentSubType(beneficiary.documentSubType);
      if (beneficiary.uploadedDocuments) {
        setUploadedDocuments(beneficiary.uploadedDocuments);
      }
      if (beneficiary.documentFileList) {
        setDocumentFileList(beneficiary.documentFileList);
      }

      const docs = Array.isArray(data?.data?.documents)
        ? data.data.documents
        : [];
      const mapped = docs.map((d) => {
        const selectedProof = ProofTypeOptions.find(
          (o) => o.value === d.documentType
        );
        const typeLabel = selectedProof?.label || `${d.documentType}`;
        const subLabel =
          (selectedProof?.subTypeOptions || []).find(
            (o) => o.value === d.documentSubType
          )?.label || `${d.documentSubType}`;
        return {
          id: d.id || Date.now(),
          documentType: typeLabel,
          documentName: subLabel,
          fileName: d.fileName || "document",
          file: { url: d.fileUrlPath },
          status: "UPLOADED",
          proofType: d.documentType,
          subType: d.documentSubType,
        };
      });
      if (mapped.length > 0) {
        setUploadedDocuments(mapped);
      }
    } catch (error) {
      console.error("Error fetching beneficiary info:", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (record) => {
    if (isDisabled) return;
    setSelectedBeneficiaryForDelete(record);
    setIsDeleteConfirmModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedBeneficiaryForDelete) return;

    try {
      setIsDeleteLoading(true);
      await deleteBeneficialInfoApi(selectedBeneficiaryForDelete.id);
      const updated = beneficiariesList.filter(
        (b) => b.id !== selectedBeneficiaryForDelete.id
      );
      setBeneficiariesList(updated);
      toast.success(t("Beneficiary deleted successfully!"));
      setIsDeleteConfirmModal(false);
      setSelectedBeneficiaryForDelete(null);
    } catch (error) {
      console.error("Error deleting beneficiary:", error);
      handleApiError(error);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (isDisabled) return;
    try {
      const taxDomicilesArray = values?.taxDomiciles || [];
      delete values?.taxDomiciles;
      const type = values.type || beneficiaryType;

      if (!type) {
        toast.error(t("Please select beneficiary type"));
        return;
      }

      setIsSubmitting(true);
      const formData = new FormData();

      taxDomicilesArray.forEach((item, index) => {
        formData.append(
          `taxDomiciles[${index}].taxDomicileCountryId`,
          String(item?.taxDomicileCountryId ?? "")
        );
        formData.append(
          `taxDomiciles[${index}].isTINIDAvailable`,
          String(item?.isTINIDAvailable ?? false)
        );
        if (item?.isTINIDAvailable) {
          formData.append(
            `taxDomiciles[${index}].taxDomicileReasonID`,
            String(item?.taxDomicileReasonID ?? "")
          );
          formData.append(`taxDomiciles[${index}].taxIDNumber`, "");
          formData.append(
            `taxDomiciles[${index}].alternateTaxIdentifier`,
            String(item?.alternateTaxIdentifier ?? "")
          );
        } else {
          formData.append(`taxDomiciles[${index}].taxDomicileReasonID`, "");
          formData.append(
            `taxDomiciles[${index}].taxIDNumber`,
            String(item?.taxIDNumber ?? "")
          );
        }
      });

      const beneficiaryId =
        editingIndex !== null && beneficiariesList[editingIndex]?.id
          ? beneficiariesList[editingIndex].id
          : null;
      if (beneficiaryId) {
        formData.append("Id", beneficiaryId);
      }

      formData.append("customerTypeId", type);
      formData.append("ownershipPercentage", values.ownershipPercentage || "0");

      if (type === 1) {
        // Individual fields
        if (values.firstName) formData.append("firstName", values.firstName);
        if (values.lastName) formData.append("lastName", values.lastName);
        if (values.dateOfBirth) formData.append("dob", values.dateOfBirth);
        if (values.email) formData.append("email", values.email);
        if (values.mobileCountryCode && values.mobileNumber) {
          formData.append(
            "mobileNumber",
            `${values.mobileCountryCode} ${values.mobileNumber}`.trim()
          );
        }
        if (
          values.alternativeMobileCountryCode &&
          values.alternativeMobileNumber
        ) {
          formData.append(
            "alternativePhoneNumber",
            `${values.alternativeMobileCountryCode} ${values.alternativeMobileNumber}`.trim()
          );
        }
        if (values.nationality)
          formData.append("nationality", values.nationality);
        if (values.countryOfBirth)
          formData.append("countryOfBirth", values.countryOfBirth);
        if (values.countryOfResidence)
          formData.append("countryOfResidence", values.countryOfResidence);
        if (values.countryOfTaxResidence)
          formData.append(
            "countryOfTaxResidence",
            values.countryOfTaxResidence
          );
        if (values.apartmentVillaNumber)
          formData.append("apartmentVillaInfo", values.apartmentVillaNumber);
        if (values.streetNameArea)
          formData.append("streetAreaInfo", values.streetNameArea);
        if (values.registeredAddressCountry)
          formData.append("countryCode", values.registeredAddressCountry);
        if (values.cityTown) formData.append("cityTown", values.cityTown);
        if (values.postalZipCode)
          formData.append("postalZipCode", values.postalZipCode);
        if (isUSPerson !== null) formData.append("areYouUSPerson", isUSPerson);
        if (isUSPerson && values.socialSecurityNumber)
          formData.append("socialSecurityNumber", values.socialSecurityNumber);
        if (isPEP !== null)
          formData.append("isPoliticallyExposedPerson", isPEP);
        if (isPEP && values.pepDetails)
          formData.append("pepDetails", values.pepDetails);

        // Documents
        uploadedDocuments.forEach((doc, index) => {
          const fileObj = doc?.file?.originFileObj || doc?.file;
          const idToUse = doc?.id;
          if (fileObj instanceof Blob) {
            const fname = fileObj?.name || doc?.fileName || "document";
            formData.append(`documents[${index}].file`, fileObj, fname);
            formData.append(`documents[${index}].fileUrlPath`, "");
          } else {
            formData.append(`documents[${index}].id`, idToUse);
            const urlPath =
              (doc?.file && doc.file.url) || doc?.fileUrlPath || "";
            formData.append(`documents[${index}].fileUrlPath`, urlPath);
          }
          formData.append(`documents[${index}].documentType`, doc.proofType);
          formData.append(`documents[${index}].documentSubType`, doc.subType);
        });
      } else if (type === 2) {
        // Corporate fields
        if (values.companyName)
          formData.append("CompanyName", values.companyName);
        if (values.dateOfIncorporation)
          formData.append("dateOfIncorporation", values.dateOfIncorporation);
        if (values.countryOfIncorporation)
          formData.append(
            "countryOfIncorporation",
            values.countryOfIncorporation
          );
        if (values.companyRegistrationNumber)
          formData.append(
            "companyRegistrationNumber",
            values.companyRegistrationNumber
          );

        // Find nature of business ID from API options or hardcoded options
        let natureOfBusinessId = null;
        if (natureOfBusinessApiOptions.length > 0) {
          natureOfBusinessId =
            natureOfBusinessApiOptions.find(
              (opt) =>
                opt.label === values.natureOfBusiness ||
                opt.value === values.natureOfBusiness
            )?.value || null;
        } else {
          // Fallback: try to find by label in hardcoded options and map to API
          const hardcodedOption = natureOfBusinessApiOptions.find(
            (opt) => opt.label === values.natureOfBusiness
          );
          if (hardcodedOption) {
            // Try to find matching API option by label
            natureOfBusinessId =
              natureOfBusinessApiOptions.find(
                (opt) => opt.label === hardcodedOption.label
              )?.value || null;
          }
        }
        if (natureOfBusinessId) {
          formData.append("natureOfBusinessId", natureOfBusinessId);
        }
        if (values.briefDescription)
          formData.append("businessActivity", values.briefDescription);
        if (values.companyWebsite)
          formData.append("companyWebsite", values.companyWebsite);
        if (values.companyEmail)
          formData.append("companyEmail", values.companyEmail);
        if (values.phoneCountryCode && values.phoneNumber) {
          formData.append(
            "companyPhoneNumber",
            `${values.phoneCountryCode} ${values.phoneNumber}`.trim()
          );
        }
        if (values.hasBusinessInUS !== undefined) {
          formData.append("isBusinessInUS", values.hasBusinessInUS);
        }
        if (values.companyTaxResidency)
          formData.append("companyTaxResidency", values.companyTaxResidency);
        if (values.legalEntityIdentifier)
          formData.append(
            "legalEntityIdentifier",
            values.legalEntityIdentifier
          );

        // Registered Office Address
        if (values.registeredOfficeAddress)
          formData.append(
            "companyApartmentVillaInfo",
            values.registeredOfficeAddress
          );
        if (values.registeredStreetName)
          formData.append("companyStreetAreaInfo", values.registeredStreetName);
        if (values.registeredCountry)
          formData.append("companyCountryCode", values.registeredCountry);
        if (values.registeredCityTown)
          formData.append("companyCity", values.registeredCityTown);
        if (values.registeredPostalZipCode)
          formData.append("companyZipCode", values.registeredPostalZipCode);

        // Correspondence Address
        if (sameAsAbove !== undefined)
          formData.append("isSameAsAbove", sameAsAbove);
        if (values.correspondenceOfficeAddress)
          formData.append(
            "businessApartmentVillaInfo",
            values.correspondenceOfficeAddress
          );
        if (values.correspondenceStreetName)
          formData.append(
            "businessStreetAreaInfo",
            values.correspondenceStreetName
          );
        if (values.correspondenceCountry)
          formData.append("businessCountryCode", values.correspondenceCountry);
        if (values.correspondenceCityTown)
          formData.append("businessCity", values.correspondenceCityTown);
        if (values.correspondencePostalZipCode)
          formData.append(
            "businessZipCode",
            values.correspondencePostalZipCode
          );
      }

      const { data } = await addUpdateBeneficialInfo(formData);
      if (data?.success) {
        toast.success(
          data?.message || t("Beneficiary information saved successfully")
        );

        // const formattedValues = {
        //   ...values,
        //   dateOfIncorporation: values.dateOfIncorporation
        //     ? values.dateOfIncorporation.format("DD/MM/YYYY")
        //     : null,
        //   dateOfBirth: values.dateOfBirth
        //     ? values.dateOfBirth.format("DD/MM/YYYY")
        //     : null,
        //   name:
        //     values.companyName ||
        //     `${values.firstName || ""} ${values.lastName || ""}`.trim() ||
        //     "N/A",
        //   email: values.companyEmail || values.email || "N/A",
        //   id: data?.data?.id || beneficiaryId || Date.now(),
        // };

        // if (editingIndex !== null) {
        //   const updated = [...beneficiariesList];
        //   updated[editingIndex] = {
        //     ...updated[editingIndex],
        //     ...formattedValues,
        //   };
        //   setBeneficiariesList(updated);
        // } else {
        //   const newBeneficiary = {
        //     id: data?.data?.id || Date.now(),
        //     ...formattedValues,
        //   };
        //   setBeneficiariesList([...beneficiariesList, newBeneficiary]);
        // }
        await getAllBeneficialInfoData();
        form.resetFields();
        setDrawerOpen(false);
        setEditingIndex(null);
        setBeneficiaryType(null);
        setHasOtherCrsTaxResident(false);
        setAdditionalCrsTaxResidents([]);
        setSameAsAbove(false);
        setIsUSPerson(null);
        setAcceptDeclaration(false);
        setIsPEP(null);
        setDocumentFileList([]);
        setUploadedDocuments([]);
        setProofType(null);
        setDocumentSubType(null);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setDrawerOpen(false);
    setEditingIndex(null);
    setBeneficiaryType(null);
    setHasOtherCrsTaxResident(false);
    setAdditionalCrsTaxResidents([]);
    setSameAsAbove(false);
    setIsUSPerson(null);
    setAcceptDeclaration(false);
    setIsPEP(null);
    setDocumentFileList([]);
    setUploadedDocuments([]);
    setProofType(null);
    setDocumentSubType(null);
    form.resetFields();
  };

  if (isLoading) return <Loader />;

  return (
    <div className="max-w-5xl mx-auto border border-gray-300 rounded-lg p-6 mb-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">{t("Beneficiary Information")}</h3>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            size="large"
            disabled={isDisabled}
          >
            {t("Add Beneficiary Information")}
          </Button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          {t("Please provide details of any person who ultimately owns 25% or more of the company, or any person who exercises control by making financial and operating decisions for the company.")}
        </p>

        <TableWithSkeleton
          columns={beneficiariesColumns}
          data={beneficiariesList}
          loading={false}
          rowKey="id"
        />
      </div>

      <Drawer
        title={editingIndex !== null ? t("Edit Beneficiary") : t("Add Beneficiary")}
        placement="right"
        onClose={handleClose}
        open={drawerOpen}
        width={900}
        maskClosable={false}
        rootClassName="onboarding-drawer"
        style={{ overflowY: "auto" }}
        loading={loading}
      >
        <Form
          form={form}
          size="large"
          layout="vertical"
          autoComplete="off"
          disabled={isDisabled}
          onFinish={handleSubmit}
          initialValues={{
            isMultiTaxDomicileEnabled: false,
            taxDomiciles: [
              {
                taxDomicileCountryId: null,
                taxIDNumber: "",
                isTINIDAvailable: false,
                taxDomicileReasonID: null,
              },
            ],
          }}
        >
          <Form.Item
            label={t("Type")}
            name="type"
            rules={[{ required: true, message: t("Please select type") }]}
            className="mb-4"
            disabled={editingIndex !== null}
          >
            <Select
              placeholder={t("Select type")}
              onChange={handleTypeChange}
              disabled={editingIndex !== null}
              options={[
                { value: 1, label: t("Individual") },
                { value: 2, label: t("Corporate") },
              ]}
            />
          </Form.Item>

          {beneficiaryType === 2 && (
            <CorporateBeneficiaryForm
              countries={countries}
              countryCodes={countryCodes}
              natureOfBusinessOptions={natureOfBusinessApiOptions}
              tinReasonOptions={taxDomicileReasons}
              hasOtherCrsTaxResident={hasOtherCrsTaxResident}
              sameAsAbove={sameAsAbove}
              filterOption={filterOption}
              handleRegisteredAddressChange={handleRegisteredAddressChange}
              handleSameAsAboveChange={handleSameAsAboveChange}
              handleMultiTaxResidentChange={handleMultiTaxResidentChange}
              isDisabled={isDisabled}
            />
          )}

          {beneficiaryType === 1 && (
            <IndividualBeneficiaryForm
              countries={countries}
              countryCodes={countryCodes}
              tinReasonOptions={taxDomicileReasons}
              proofTypeOptions={ProofTypeOptions}
              hasOtherCrsTaxResident={hasOtherCrsTaxResident}
              additionalCrsTaxResidents={additionalCrsTaxResidents}
              isUSPerson={isUSPerson}
              handleAddDocument={handleAddDocument}
              previewUrl={previewUrl}
              acceptDeclaration={acceptDeclaration}
              isPEP={isPEP}
              documentFileList={documentFileList}
              proofType={proofType}
              documentSubType={documentSubType}
              uploadedDocuments={uploadedDocuments}
              documentColumns={documentColumns}
              filterOption={filterOption}
              handleUSPersonChange={handleUSPersonChange}
              setAcceptDeclaration={setAcceptDeclaration}
              handlePEPChange={handlePEPChange}
              handleProofTypeChange={handleProofTypeChange}
              handleDocumentSubTypeChange={handleDocumentSubTypeChange}
              handleDocumentChange={handleDocumentChange}
              handleRemoveDocument={handleRemoveDocument}
              openPdf={openPdf}
              getFileUrl={getFileUrl}
              setDocumentFileList={setDocumentFileList}
              setUploadedDocuments={setUploadedDocuments}
              handleMultiTaxResidentChange={handleMultiTaxResidentChange}
              isDisabled={isDisabled}
            />
          )}

          <div className="flex justify-end gap-2 mt-6 pt-6 border-t border-gray-200">
            <Button onClick={handleClose} disabled={isSubmitting}>
              {t("Cancel")}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              disabled={isDisabled || isSubmitting}
            >
              {editingIndex !== null ? t("Update") : t("Add")}
            </Button>
          </div>
        </Form>
      </Drawer>

      <div className="flex justify-between mt-8">
        <Button onClick={onPrevious}>{t("Previous")}</Button>
        <div>
          <Button
            type="primary"
            onClick={onNext}
            disabled={!beneficiariesList || beneficiariesList?.length === 0}
          >
            {t("Next")}
          </Button>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isDeleteConfirmModal}
        setIsOpen={setIsDeleteConfirmModal}
        type="error"
        message={`${t("Are you sure you want to delete")} "${
          selectedBeneficiaryForDelete?.name || t("this beneficiary")
        }"?`}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleteLoading}
        loadingLabel={t("Deleting beneficiary...")}
      />
    </div>
  );
};

export default BeneficiaryInfo;
